/* eslint-disable react/require-default-props */
import { Alert } from '@material-ui/lab';
import SelectTemplate from '@src/components/Template/SelectTemplate';
import { useGetContactTemplatesQuery } from '@src/reducers/contactsApi';
import { TemplatedMsgWrapper } from '@src/types';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

type ContactTemplatesSelectProps = {
  templateContext: {
    botAccountId: number;
    contactId: number;
  };
  onTemplateChange?: (template: TemplatedMsgWrapper | null) => void;
};

const ContactTemplatesSelect = ({
  templateContext,
  onTemplateChange,
}: ContactTemplatesSelectProps) => {
  const [selectedTemplate, setSelectedTemplate] =
    useState<TemplatedMsgWrapper | null>(null);
  const [isShowTemplateList, setIsShowTemplateList] = useState(false);

  const { data: contactTemplates = [], isFetching: isFetchingTemplates } =
    useGetContactTemplatesQuery(templateContext, {
      skip: !templateContext.botAccountId,
    });

  const isContactTemplatesEmpty =
    !contactTemplates?.length && !isFetchingTemplates;

  const onChangeSelectedTemplate = useCallback(
    (templateId: number) => {
      const template = contactTemplates.find(
        tm => tm.templatedMsg.id === templateId
      );
      if (template) {
        setSelectedTemplate(template);
        setIsShowTemplateList(false);
        onTemplateChange?.(template);
      }
    },
    [contactTemplates, onTemplateChange]
  );

  const toggleShowTemplateList = useCallback(() => {
    setIsShowTemplateList(prev => !prev);
  }, []);

  useEffect(() => {
    const defaultTemplate = contactTemplates.find(
      ({ templatedMsg }) => templatedMsg.byDefault
    );
    const initialTemplate = defaultTemplate || contactTemplates[0];

    setSelectedTemplate(!contactTemplates.length ? null : initialTemplate);
    onTemplateChange?.(!contactTemplates.length ? null : initialTemplate);
  }, [contactTemplates, onTemplateChange, templateContext.botAccountId]);

  if (isContactTemplatesEmpty) {
    return (
      <Alert severity="info">
        <FormattedMessage id="TemplatedMsg.templateNoAvailable" />
      </Alert>
    );
  }

  if (!selectedTemplate) return null;

  return (
    <SelectTemplate
      onSelectedTemplate={onChangeSelectedTemplate}
      selectedTemplate={selectedTemplate}
      templatedMsgs={contactTemplates}
      showTemplates={isShowTemplateList}
      onShowTemplates={toggleShowTemplateList}
      allowClass={false}
    />
  );
};

export default ContactTemplatesSelect;
